import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AdminAuthService } from '../services/firebase';

interface AppState {
  user: any;
  isAuthenticated: boolean;
  isLoading: boolean;
  theme: 'light' | 'dark';
  notifications: any[];
}

interface AppContextType {
  state: AppState;
  setUser: (user: any) => void;
  setTheme: (theme: 'light' | 'dark') => void;
  addNotification: (notification: any) => void;
  removeNotification: (id: string) => void;
  logout: () => Promise<void>;
  refreshAuth: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [state, setState] = useState<AppState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    theme: 'light',
    notifications: []
  });

  // Check authentication state on app start
  useEffect(() => {
    console.log('🔍 Initializing app authentication...');
    
    const unsubscribe = AdminAuthService.onAuthStateChanged((user) => {
      console.log('🔄 Auth state changed:', user ? 'Authenticated' : 'Not authenticated');
      
      // Simplified auth state management - primarily trust Firebase
      const shouldBeAuthenticated = !!user;
      
      if (shouldBeAuthenticated) {
        // Ensure localStorage is in sync
        localStorage.setItem('isAuthenticated', 'true');
      } else {
        // Clear localStorage when no user
        localStorage.removeItem('isAuthenticated');
      }
      
      setState(prev => ({
        ...prev,
        user: shouldBeAuthenticated ? user : null,
        isAuthenticated: shouldBeAuthenticated,
        isLoading: false
      }));
      
      if (shouldBeAuthenticated) {
        console.log('✅ Authentication established');
      }
    });

    // Check current user immediately
    const currentUser = AdminAuthService.getCurrentUser();
    
    if (currentUser) {
      console.log('✅ Current user found:', currentUser.email);
      localStorage.setItem('isAuthenticated', 'true');
      setState(prev => ({
        ...prev,
        user: currentUser,
        isAuthenticated: true,
        isLoading: false
      }));
    } else {
      console.log('ℹ️ No current user found');
      localStorage.removeItem('isAuthenticated');
      setState(prev => ({
        ...prev,
        user: null,
        isAuthenticated: false,
        isLoading: false
      }));
    }

    // Set up activity tracking for session management
    const trackActivity = () => AdminAuthService.updateLastActivity();
    
    const events = ['click', 'keydown', 'mousemove', 'scroll'];
    events.forEach(event => {
      window.addEventListener(event, trackActivity, { passive: true });
    });

    return () => {
      unsubscribe();
      events.forEach(event => {
        window.removeEventListener(event, trackActivity);
      });
    };
  }, []);

  const setUser = (user: any) => {
    setState(prev => ({ 
      ...prev, 
      user,
      isAuthenticated: !!user
    }));
  };

  const setTheme = (theme: 'light' | 'dark') => {
    setState(prev => ({ ...prev, theme }));
  };

  const addNotification = (notification: any) => {
    setState(prev => ({ 
      ...prev, 
      notifications: [...prev.notifications, notification] 
    }));
  };

  const removeNotification = (id: string) => {
    setState(prev => ({ 
      ...prev, 
      notifications: prev.notifications.filter(n => n.id !== id) 
    }));
  };

  const logout = async () => {
    try {
      console.log('🚪 Initiating admin logout...');
      await AdminAuthService.logout();
      
      setState(prev => ({
        ...prev,
        user: null,
        isAuthenticated: false,
        notifications: [] // Clear notifications on logout
      }));
      
      // Dispatch logout event for any other components listening
      window.dispatchEvent(new CustomEvent('adminLogout'));
      
      console.log('✅ Admin logout completed');
    } catch (error) {
      console.error('❌ Logout error:', error);
      
      // Even if logout fails, clear local state
      setState(prev => ({
        ...prev,
        user: null,
        isAuthenticated: false
      }));
    }
  };

  const refreshAuth = () => {
    const currentUser = AdminAuthService.getCurrentUser();
    setState(prev => ({
      ...prev,
      user: currentUser,
      isAuthenticated: !!currentUser,
      isLoading: false
    }));
  };

  const value: AppContextType = {
    state,
    setUser,
    setTheme,
    addNotification,
    removeNotification,
    logout,
    refreshAuth
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
};

export default AppContext;